"use client";

import { Button } from "@/components/ui/button";
import { createPath } from "@/service/routing/routing";
import { PlayIcon } from "lucide-react";
import { SuperLink as Link } from "@/components/common/super-link";
import { PictureUpload } from "@/components/common/picture-upload/picture-upload";
import { Separator } from "@/components/ui/separator";
import { ClientContentBlocks } from "@/components/common/content-blocks/client-content-blocks";
import { LinkToBackoffice } from "@/components/common/link-to-backoffice";
import { SponsoredBadge } from "@/components/common/sponsored-badge";

export const ClinicalCaseBeforeStart = ({
  clinicalCase,
  onStartVisit,
}: {
  clinicalCase: ClinicalCaseVM;
  onStartVisit?: () => void;
}) => {
  const {
    id,
    category,
    name,
    isSponsored,
    descriptionBlocks,
    image,
    steps,
    verticalImage,
  } = clinicalCase;

  return (
    <div className="flex flex-col items-center justify-center">
      {isSponsored && <SponsoredBadge />}

      <div className="flex max-w-[600px] flex-col justify-between overflow-hidden rounded-xl border bg-white p-4 shadow md:p-8">
        <div>
          <div className="flex items-center justify-between gap-8 sm:pb-8">
            <header className="flex flex-col gap-2">
              {category && (
                <Link
                  href={createPath("clinical_case.category", {
                    categorySlug: category.slug,
                  })}
                >
                  <div className="text-remedium-blue text-xs font-medium uppercase hover:underline">
                    {category.name}
                  </div>
                </Link>
              )}

              <h1 className="text:lg font-semibold sm:text-xl">{name}</h1>

              <LinkToBackoffice params={{ id }} type={"clinical_case"} />

              <div className="relative my-2 flex shrink-0 items-center self-stretch sm:hidden">
                <PictureUpload
                  image={verticalImage || image}
                  aspectRatio="1.91 / 1"
                  className="rounded-lg"
                />
              </div>
              <Button
                variant={"blue"}
                className="my-4 mb-8 sm:hidden"
                onClick={onStartVisit}
              >
                <PlayIcon className="h-4 w-4" />
                Rozpocznij wizytę
              </Button>
            </header>
            <div className="relative hidden w-1/3 shrink-0 items-center self-stretch sm:flex">
              <PictureUpload
                image={verticalImage || image}
                aspectRatio="1.91 / 1"
                className="rounded-lg"
              />
            </div>
          </div>
          <div className="overflow-x-hidden overflow-y-auto">
            <ClientContentBlocks
              blocks={descriptionBlocks}
              theme="clinical_case_description"
            />
          </div>
        </div>
        <div className="flex flex-col gap-6">
          <Button
            variant={"blue"}
            className="mt-4 hidden sm:flex"
            onClick={onStartVisit}
          >
            <PlayIcon className="h-4 w-4" />
            Rozpocznij wizytę
          </Button>
          <div className="relative flex h-[20px] w-full items-center justify-center">
            <Separator className="w-full" />
            <p className="text-muted-foreground absolute top-0 left-0 flex h-full w-full items-center justify-center bg-transparent text-center text-xs">
              <span className="bg-white px-2 text-center">
                {steps} {steps === 1 ? "etap" : steps < 5 ? "etapy" : "etapów"}
              </span>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
